'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import ToastContainer from '@/components/ui/Toast';
import DashboardKPIs from './components/DashboardKPIs';
import DashboardCharts from './components/DashboardCharts';
import DashboardAlerts from './components/DashboardAlerts';
import DashboardActivity from './components/DashboardActivity';
import { ScanLine, X, AlertCircle, ExternalLink, Clock, FileText, Tag, QrCode } from 'lucide-react';
import Link from 'next/link';
import StatusBadge from '@/components/ui/StatusBadge';
import { PrintJob } from '@/lib/mockData';

export const dynamic = 'force-dynamic';

const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://printtrace2624.builtwithrocket.new';

function passportUrl(jobId: string) {
  return `${siteUrl}/passport/${jobId}`;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getAllPatients(job: PrintJob): { id: string | null; name: string }[] {
  const result: { id: string | null; name: string }[] = [];
  if (job.patient_name) result.push({ id: job.patient_id, name: job.patient_name });
  for (const p of job.additional_patients ?? []) {
    result.push({ id: p.id, name: p.name });
  }
  return result;
}

function BarcodeScanTile() {
  const [jobs, setJobs] = useState<PrintJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [scanValue, setScanValue] = useState('');
  const [result, setResult] = useState<PrintJob | null | 'not_found'>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoadingJobs(true);
    fetch('/api/print-jobs')
      .then((r) => r.json())
      .then((data: { jobs: PrintJob[] }) => setJobs(data.jobs || []))
      .catch(() => {})
      .finally(() => setLoadingJobs(false));
  }, []);

  const handleSearch = useCallback(
    (value: string) => {
      setScanValue(value);
      if (!value.trim()) {
        setResult(null);
        return;
      }
      const q = value.trim().toLowerCase();
      const found = jobs.find((j) => {
        const url = passportUrl(j.id).toLowerCase();
        const shortId = j.id.slice(-8).toLowerCase();
        const ptId = ('pt-' + shortId).toLowerCase();
        return (
          url.includes(q) ||
          j.id.toLowerCase() === q ||
          j.shining_job_id.toLowerCase() === q ||
          shortId === q ||
          ('#' + shortId) === q ||
          ptId === q
        );
      });
      setResult(found ?? 'not_found');
    },
    [jobs]
  );

  const handleClear = () => {
    setScanValue('');
    setResult(null);
    inputRef.current?.focus();
  };

  return (
    <div
      className="rounded-2xl border p-5 flex flex-col gap-4"
      style={{
        background: 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(14,165,233,0.05) 100%)',
        borderColor: 'rgba(59,130,246,0.3)',
        boxShadow: '0 0 0 1px rgba(59,130,246,0.08)',
      }}
    >
      {/* Tile header */}
      <div className="flex items-center gap-3">
        <div
          className="flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0"
          style={{ backgroundColor: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)' }}
        >
          <QrCode size={20} style={{ color: '#3b82f6' }} />
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
            Traçabilité — Scan code-barres
          </p>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            Scannez ou saisissez un code pour retrouver un passeport ou une fiche
          </p>
        </div>
      </div>

      {/* Input */}
      <div className="relative">
        <ScanLine
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2"
          style={{ color: '#3b82f6' }}
        />
        <input
          ref={inputRef}
          type="text"
          placeholder={loadingJobs ? 'Chargement des données…' : 'Scanner ou saisir le code-barres…'}
          value={scanValue}
          onChange={(e) => handleSearch(e.target.value)}
          disabled={loadingJobs}
          autoComplete="off"
          className="w-full pl-10 pr-10 py-3 rounded-xl border text-sm outline-none transition-colors font-mono"
          style={{
            backgroundColor: 'var(--card)',
            borderColor: scanValue ? 'rgba(59,130,246,0.6)' : 'rgba(59,130,246,0.25)',
            color: 'var(--foreground)',
            fontSize: '0.875rem',
          }}
        />
        {scanValue && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:opacity-70"
            style={{ color: 'var(--muted-foreground)' }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Hint */}
      {!scanValue && (
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          Accepte : ID court <span className="font-mono">#XXXXXXXX</span>, ID complet, URL passeport ou code douchette
        </p>
      )}

      {/* Not found */}
      {result === 'not_found' && (
        <div
          className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
          style={{ backgroundColor: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)' }}
        >
          <AlertCircle size={14} style={{ color: 'var(--danger)' }} />
          <span className="text-sm" style={{ color: 'var(--danger)' }}>
            Aucun résultat trouvé pour ce code.
          </span>
        </div>
      )}

      {/* Found */}
      {result && result !== 'not_found' && (
        <div
          className="rounded-xl border p-4 space-y-3"
          style={{ backgroundColor: 'var(--card)', borderColor: 'rgba(16,185,129,0.35)' }}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                  {getAllPatients(result).map((p) => p.name).join(', ') || '—'}
                </p>
                <StatusBadge label="Trouvé" variant="success" />
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <FileText size={11} style={{ color: 'var(--muted-foreground)' }} />
                <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>
                  {(result.file_name ?? '').replace(/\.(slp3|stl|obj|3mf)$/i, '') || '—'}
                </p>
              </div>
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  <Clock size={12} />
                  {formatDate(result.printed_at)}
                </span>
                {result.material_type && (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: 'var(--surface)', color: 'var(--muted-foreground)' }}
                  >
                    {result.material_brand} {result.material_type}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
              <Link
                href={`/print-job-detail?id=${result.id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
                style={{
                  backgroundColor: 'rgba(14,165,233,0.12)',
                  color: 'var(--primary)',
                  border: '1px solid rgba(14,165,233,0.3)',
                }}
              >
                <Tag size={13} />
                Voir la fiche
              </Link>
              <a
                href={`/passport/${result.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
                style={{
                  backgroundColor: 'rgba(16,185,129,0.12)',
                  color: 'var(--success)',
                  border: '1px solid rgba(16,185,129,0.3)',
                }}
              >
                <ExternalLink size={13} />
                Voir passeport
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: 'var(--foreground)' }}>
              Tableau de bord
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
              AccuFab F1 — Données en temps réel
            </p>
          </div>
          <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: 'var(--success)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
            <span className="w-1.5 h-1.5 rounded-full pulse-glow" style={{ backgroundColor: 'var(--success)' }} />
            Imprimante en ligne
          </span>
        </div>

        {/* Barcode scan tile — always visible */}
        <BarcodeScanTile />

        {/* KPI bento grid */}
        <DashboardKPIs />

        {/* Charts row */}
        <DashboardCharts />

        {/* Alerts + Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <DashboardAlerts />
          </div>
          <div>
            <DashboardActivity />
          </div>
        </div>
      </div>
      <ToastContainer />
    </AppLayout>
  );
}